import { FC, useCallback } from 'react';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';

import { Box, Chip, GridColDef } from '@thirdspacelearning/library';

import DraggableGridRow from './DraggableGridRow';
import DragHandleIcon from './DragHandleIcon';

import { CMSDataGrid } from '@shared';
import {
    Country,
    GBLearningObjective,
    LearningObjective,
    USLearningObjective,
} from '@types';
import { yearGroupSortComparator } from '../../../utils';

type Props = {
    sortedLOs: LearningObjective[];
    setSortedLOs: (sortedLOs: LearningObjective[]) => void;
    country: Country;
};

const ProgrammeLoReorderTable: FC<Props> = ({
    sortedLOs,
    setSortedLOs,
    country,
}) => {
    const sensors = useSensors(useSensor(PointerSensor));

    const GBColumns: GridColDef<GBLearningObjective>[] = [
        {
            field: 'yearGroup',
            type: 'string',
            headerName: 'Year group',
            headerAlign: 'center',
            align: 'center',
            sortingOrder: ['asc', 'desc'],
            sortComparator: yearGroupSortComparator,
            renderCell: ({ row }) =>
                row.year_id ? (
                    <Chip
                        label={`Year ${row.year_id}`}
                        variant="outlined"
                        size="small"
                    />
                ) : (
                    '-'
                ),
            sortable: false,
            minWidth: 110,
        },
    ];

    const USColumns: GridColDef<USLearningObjective>[] = [
        {
            field: 'yearGroup',
            type: 'string',
            headerName: 'Grade',
            headerAlign: 'center',
            align: 'center',
            sortingOrder: ['asc', 'desc'],
            sortComparator: yearGroupSortComparator,
            renderCell: ({ row }) =>
                row.year_id ? (
                    <Chip
                        label={`Grade ${row.year_id}`}
                        variant="outlined"
                        size="small"
                    />
                ) : (
                    '-'
                ),
            sortable: false,
            minWidth: 110,
        },
    ];

    const columns: GridColDef<LearningObjective>[] = [
        {
            field: 'icon',
            headerName: '',
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            minWidth: 40,
            maxWidth: 40,
            renderCell: () => <DragHandleIcon />,
        },
        {
            field: 'title',
            type: 'string',
            headerName: 'Learning objective',
            renderCell: ({ row }) => (
                <Box sx={{ whiteSpace: 'normal' }}>
                    {row.ai && (
                        <Chip
                            label={'AI'}
                            color="info"
                            size="small"
                            sx={{ mb: '2px', mr: 1 }}
                        />
                    )}
                    {row.title}
                </Box>
            ),
            sortable: false,
            minWidth: 400,
            flex: 1,
        },
        ...(country === Country.GB
            ? (GBColumns as GridColDef<LearningObjective>[])
            : (USColumns as GridColDef<LearningObjective>[])),
        {
            field: 'id',
            type: 'string',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            sortable: false,
        },
    ];

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (over) {
                const oldIndex = sortedLOs.findIndex(
                    (los) => los.id === active.id,
                );
                const newIndex = sortedLOs.findIndex(
                    (los) => los.id === over.id,
                );
                setSortedLOs(arrayMove(sortedLOs, oldIndex, newIndex));
            }
        },
        [sortedLOs],
    );

    const loIds = sortedLOs.map((lo) => lo.id);

    return (
        sortedLOs && (
            <DndContext
                sensors={sensors}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext items={loIds}>
                    <CMSDataGrid
                        columns={columns}
                        rows={sortedLOs}
                        disableColumnMenu
                        disableColumnSelector
                        disableColumnFilter
                        disableDensitySelector
                        disableRowSelectionOnClick
                        disableColumnResize
                        hideFooter
                        slots={{ row: DraggableGridRow }}
                    />
                </SortableContext>
            </DndContext>
        )
    );
};

export default ProgrammeLoReorderTable;
